import { Stack } from '@evaneos/b2b-ui';

import { DossierId, Heat } from '../../../../resources/dossier/types';
import { useGetDossierData } from '../../../../resources/dossier/queries';
import { useUpdateHeatLevelMutation } from '../../../../resources/dossier/mutations';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { HeatChipClickable } from '../../../../shared/components/DossierQualification/HeatChipClickable';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';

type HeatLevelEditableItemProps = {
    dossierId: DossierId;
};

export function HeatLevelEditableItem({ dossierId }: Readonly<HeatLevelEditableItemProps>) {
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const { heatLevel } = useGetDossierData(dossierId)?.dossier || {};
    const updateHeatLevelMutation = useUpdateHeatLevelMutation(dossierId);

    const handleChange = (newHeatLevel: Heat) => {
        const nextHeatLevel = newHeatLevel === heatLevel ? Heat.NONE : newHeatLevel;
        updateHeatLevelMutation.mutate({ heatLevel: nextHeatLevel });

        trackEvent((ampli, defaultProperties) => {
            if (!heatLevel) return;
            const heatLabelMap = {
                [Heat.NONE]: 'none' as const,
                [Heat.HOT]: 'hot' as const,
                [Heat.WARM]: 'warm' as const,
                [Heat.COLD]: 'cold' as const,
            };
            ampli.salePotentialSelected({
                ...defaultProperties,
                ...trackedTripProperties,
                heat_level_before_sale_potential_selected: heatLabelMap[heatLevel],
                sale_potential_level: heatLabelMap[nextHeatLevel],
            });
        });
    };

    return (
        <DefinitionList.StaticValue>
            <Stack direction="row" flexWrap="wrap" gap={1}>
                <HeatChipClickable
                    isRevamped
                    heatLevel={Heat.HOT}
                    isSelected={heatLevel === Heat.HOT}
                    data-testid="heat-chip-hot"
                    onChange={handleChange}
                />
                <HeatChipClickable
                    isRevamped
                    heatLevel={Heat.WARM}
                    isSelected={heatLevel === Heat.WARM}
                    data-testid="heat-chip-warm"
                    onChange={handleChange}
                />
                <HeatChipClickable
                    isRevamped
                    heatLevel={Heat.COLD}
                    isSelected={heatLevel === Heat.COLD}
                    data-testid="heat-chip-cold"
                    onChange={handleChange}
                />
            </Stack>
        </DefinitionList.StaticValue>
    );
}
